/**
 * AdministrativoTab - Componente para relatórios administrativos
 * Inclui geração de PDF com jspdf e html2canvas
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '@/hooks/use-toast';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const AdministrativoTab = ({ notasAdministrativas = [], recebimentos = [], cotacoes = [], produtos = [], pedidos = [], onRefresh, usuario, showConfirmDialog }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [nomeNota, setNomeNota] = useState('');
  const [recebimentosSelecionados, setRecebimentosSelecionados] = useState([]);
  const [cotacaoSelecionada, setCotacaoSelecionada] = useState(''); // Nova state para cotação
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditPesosModal, setShowEditPesosModal] = useState(false);
  const [notaSelecionada, setNotaSelecionada] = useState(null);
  const [dadosConsolidados, setDadosConsolidados] = useState([]);
  const [dadosEditados, setDadosEditados] = useState({});
  const [produtoEditandoPesos, setProdutoEditandoPesos] = useState(null);
  const [pesosEditados, setPesosEditados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ordenacaoFornecedor, setOrdenacaoFornecedor] = useState(false);
  const [searchText, setSearchText] = useState(''); // Estado para pesquisa de produtos
  const [expandedProducts, setExpandedProducts] = useState(new Set()); // Produtos com filiais expandidas
  const [produtosBonificados, setProdutosBonificados] = useState([]); // Produtos marcados como bonificação
  const [bonificacoesAlteradas, setBonificacoesAlteradas] = useState(false); // Flag para mostrar botão salvar

  // Função para agrupar dados por filial
  const agruparPorFilial = (produto) => {
    const filiaisMap = new Map();
    
    // Filtrar apenas os recebimentos que pertencem à nota selecionada
    const recebimentosDaNota = notaSelecionada 
      ? recebimentos.filter(r => notaSelecionada.recebimentos_ids.includes(r.id))
      : [];
    
    // Buscar recebimentos da nota relacionados a este produto
    const recebimentosDoProduto = recebimentosDaNota.filter(rec => 
      rec.itens?.some(item => item.produto_id === produto.produto_id)
    );
    
    // Agrupar por filial
    recebimentosDoProduto.forEach(recebimento => {
      const item = recebimento.itens.find(i => i.produto_id === produto.produto_id);
      if (!item) return;
      
      // Buscar o pedido original para obter a filial e quantidade pedido
      const pedidoOriginal = pedidos.find(p => p.id === recebimento.pedido_id);
      const filial = pedidoOriginal?.filial || 'Sem filial';
      
      // Buscar quantidade pedido do pedido original
      let quantidadePedido = 0;
      if (pedidoOriginal) {
        const produtoPedido = pedidoOriginal.produtos?.find(p => 
          (p.produto_id || p.id) === produto.produto_id
        );
        quantidadePedido = produtoPedido?.quantidade || 0;
      }
      
      // Quantidade faltante do recebimento
      const quantidadeFaltante = item.quantidade_faltante || 0;
      
      // Quantidade recebida = Quantidade pedido - Quantidade faltante
      const quantidadeRecebida = quantidadePedido - quantidadeFaltante;
      
      if (filiaisMap.has(filial)) {
        const existing = filiaisMap.get(filial);
        existing.quantidade_pedido += quantidadePedido;
        existing.quantidade_recebido += quantidadeRecebida;
        existing.peso_recebido += parseFloat(item.peso) || 0;
      } else {
        filiaisMap.set(filial, {
          filial: filial,
          quantidade_pedido: quantidadePedido,
          quantidade_recebido: quantidadeRecebida,
          peso_recebido: parseFloat(item.peso) || 0
        });
      }
    });
    
    return Array.from(filiaisMap.values());
  };

  // Filtrar apenas recebimentos finalizados
  const recebimentosFinalizados = recebimentos.filter(r => r.conferido === true || r.conferido_em);

  // Função para criar nota
  const handleCriarNota = async () => {
    if (!nomeNota.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "Por favor, informe o nome da nota",
        variant: "destructive"
      });
      return;
    }

    if (recebimentosSelecionados.length === 0) {
      toast({
        title: "Selecione recebimentos",
        description: "Selecione pelo menos um recebimento",
        variant: "destructive"
      });
      return;
    }

    try {
      await axios.post('/notas-administrativas', {
        nome: nomeNota,
        recebimentos_ids: recebimentosSelecionados,
        cotacao_id: cotacaoSelecionada || null
      });

      toast({
        title: "Nota criada!",
        description: `A nota "${nomeNota}" foi criada com sucesso`
      });

      setShowAddForm(false);
      setNomeNota('');
      setRecebimentosSelecionados([]);
      setCotacaoSelecionada('');
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao criar nota",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  // Função para excluir nota
  const handleExcluirNota = async (nota) => {
    const confirmed = await showConfirmDialog(
      "Excluir Nota Administrativa",
      `Tem certeza que deseja excluir a nota "${nota.nome}"? Esta ação não poderá ser desfeita.`
    );
    
    if (!confirmed) return;

    try {
      await axios.delete(`/notas-administrativas/${nota.id}`);
      toast({
        title: "Nota excluída!",
        description: "A nota foi excluída com sucesso"
      });
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao excluir nota",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  // Função para consolidar dados dos recebimentos
  const consolidarDadosRecebimentos = (nota) => {
    const produtosMap = new Map();
    
    // Buscar recebimentos da nota
    const recebimentosDaNota = recebimentos.filter(r => nota.recebimentos_ids.includes(r.id));
    
    // Buscar pedidos originais dos recebimentos para obter quantidade pedido
    const pedidosDosRecebimentos = pedidos.filter(p => 
      recebimentosDaNota.some(r => r.pedido_id === p.id)
    );
    
    recebimentosDaNota.forEach(recebimento => {
      // Buscar o pedido original deste recebimento
      const pedidoOriginal = pedidosDosRecebimentos.find(p => p.id === recebimento.pedido_id);
      
      (recebimento.itens || []).forEach(item => {
        const produtoId = item.produto_id;
        
        // Quantidade recebida
        const quantidadeRecebida = item.quantidade || 0;
        const pesoRecebido = parseFloat(item.peso) || 0;
        
        // Buscar quantidade pedido do pedido original
        let quantidadePedido = 0;
        if (pedidoOriginal) {
          const produtoPedido = pedidoOriginal.produtos?.find(p => 
            (p.produto_id || p.id) === produtoId
          );
          quantidadePedido = produtoPedido?.quantidade || 0;
        }
        
        if (produtosMap.has(produtoId)) {
          const existing = produtosMap.get(produtoId);
          existing.quantidade_recebido += quantidadeRecebida;
          existing.quantidade_pedido += quantidadePedido;
          existing.peso_recebido += pesoRecebido;
          existing.pesosRecebimentos.push({
            recebimento_id: recebimento.id,
            numero_pedido: recebimento.numero_pedido,
            filial: pedidoOriginal?.filial || 'Sem filial',
            peso: pesoRecebido
          });
        } else {
          // Buscar dados do produto
          const produtoInfo = produtos.find(p => p.id === produtoId);
          
          // Buscar CUSTO CX e PESO CX da cotação vinculada ou última cotação
          let custoCx = 0;
          let pesoCx = 0;
          let fornecedor = 'Sem cotação';
          
          let cotacaoParaUsar = null;
          
          // Se a nota tem uma cotação vinculada, usar ela prioritariamente
          if (nota.cotacao_id) {
            cotacaoParaUsar = cotacoes.find(c => c.id === nota.cotacao_id);
          }
          
          // Se não encontrou cotação vinculada, buscar última cotação encerrada
          if (!cotacaoParaUsar) {
            const cotacoesComProduto = cotacoes
              .filter(c => c.produtos_ids && c.produtos_ids.includes(produtoId) && c.status_cotacao === 'Encerrada' && c.vencedores)
              .sort((a, b) => new Date(b.data_criacao) - new Date(a.data_criacao));
            
            if (cotacoesComProduto.length > 0) {
              cotacaoParaUsar = cotacoesComProduto[0];
            }
          }
          
          // Extrair dados do vencedor
          if (cotacaoParaUsar) {
            const vencedor = cotacaoParaUsar.vencedores?.[produtoId];
            if (vencedor) {
              custoCx = vencedor.valor_caixa || 0;
              pesoCx = vencedor.quantidade_kg || 0;
              fornecedor = vencedor.nome_fornecedor || 'N/A';
            }
          }
          
          // Se não houver cotação, buscar do cadastro do produto
          if (pesoCx === 0 && produtoInfo) {
            if (produtoInfo.unidade === 'UN') {
              // Para produtos UN, peso CX = 1 (sempre)
              pesoCx = 1;
            } else if (produtoInfo.peso_cx) {
              // Para produtos CX/SC, usar peso_cx do cadastro
              pesoCx = parseFloat(produtoInfo.peso_cx) || 0;
            }
          }
          
          // Verificar se há edições manuais
          const edicoes = nota.produtos_editados?.[produtoId];
          if (edicoes) {
            custoCx = edicoes.custo_cx !== undefined ? edicoes.custo_cx : custoCx;
            pesoCx = edicoes.peso_cx !== undefined ? edicoes.peso_cx : pesoCx;
            fornecedor = edicoes.fornecedor || fornecedor;
          }
          
          produtosMap.set(produtoId, {
            produto_id: produtoId,
            nome: produtoInfo?.nome || item.nome_produto || 'N/A',
            quantidade_recebido: quantidadeRecebida,
            quantidade_pedido: quantidadePedido,
            custo_cx: custoCx,
            peso_cx: pesoCx,
            custo_rs: pesoCx > 0 ? custoCx / pesoCx : 0,
            peso_recebido: pesoRecebido,
            fornecedor: fornecedor,
            pesosRecebimentos: [{
              recebimento_id: recebimento.id,
              numero_pedido: recebimento.numero_pedido,
              filial: pedidoOriginal?.filial || 'Sem filial',
              peso: pesoRecebido
            }]
          });
        }
      });
    });
    
    // Aplicar edições de pesos
    if (nota.pesos_editados) {
      produtosMap.forEach((produto, produtoId) => {
        const pesosEditadosProduto = nota.pesos_editados[produtoId];
        if (pesosEditadosProduto && Array.isArray(pesosEditadosProduto)) {
          // Atualizar pesos dos recebimentos
          pesosEditadosProduto.forEach(peEdit => {
            const recebIndex = produto.pesosRecebimentos.findIndex(pr => pr.recebimento_id === peEdit.recebimento_id);
            if (recebIndex >= 0) {
              produto.pesosRecebimentos[recebIndex].peso = peEdit.peso;
            }
          });
          // Recalcular peso recebido
          produto.peso_recebido = produto.pesosRecebimentos.reduce((sum, pr) => sum + pr.peso, 0);
        }
      });
    }
    
    // Aplicar edições de quantidades
    if (nota.produtos_editados) {
      produtosMap.forEach((produto, produtoId) => {
        const edicoes = nota.produtos_editados[produtoId];
        if (edicoes) {
          if (edicoes.quantidade_recebido !== undefined) {
            produto.quantidade_recebido = edicoes.quantidade_recebido;
          }
          if (edicoes.quantidade_pedido !== undefined) {
            produto.quantidade_pedido = edicoes.quantidade_pedido;
          }
        }
      });
    }
    
    // Calcular divergência para cada produto
    produtosMap.forEach((produto) => {
      // DIVERGÊNCIA = PESO RECEBIDO - (QTD PEDIDO × PESO CX)
      produto.divergencia = produto.peso_recebido - (produto.quantidade_pedido * produto.peso_cx);
    });
    
    return Array.from(produtosMap.values());
  };

  // Função para ordenar produtos por fornecedor
  const getDadosOrdenados = () => {
    if (!ordenacaoFornecedor) {
      return dadosConsolidados;
    }
    
    // Ordenar por fornecedor alfabeticamente (A-Z)
    return [...dadosConsolidados].sort((a, b) => {
      const fornA = a.fornecedor || 'Sem fornecedor';
      const fornB = b.fornecedor || 'Sem fornecedor';
      return fornA.localeCompare(fornB, 'pt-BR', { sensitivity: 'base' });
    });
  };

  // Função para exportar para impressão
  const exportarNotaParaImpressao = (nota, dados) => {
    const printWindow = window.open('', '_blank');
    
    // Obter lista de produtos bonificados da nota
    const bonificados = nota.produtos_bonificados || [];
    
    // Calcular valores
    const valorTotalBruto = dados.reduce((total, item) => {
      const custoRepasse5 = item.custo_rs * 1.05;
      return total + (custoRepasse5 * item.peso_recebido);
    }, 0);
    
    const valorBonificacoes = dados.filter(item => bonificados.includes(item.produto_id)).reduce((total, item) => {
      const custoRepasse5 = item.custo_rs * 1.05;
      return total + (custoRepasse5 * item.peso_recebido);
    }, 0);
    
    const valorTotalLiquido = valorTotalBruto - valorBonificacoes;
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Nota Administrativa - ${nota.nome}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 11pt; }
          .header { border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { font-size: 22pt; margin-bottom: 10px; }
          .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin-bottom: 20px; background-color: #f3f4f6; padding: 15px; border-radius: 5px; }
          .info-item strong { font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background-color: #e5e7eb; border: 1px solid #9ca3af; padding: 10px 8px; text-align: left; font-weight: bold; font-size: 10pt; }
          td { border: 1px solid #d1d5db; padding: 8px; font-size: 10pt; }
          tbody tr:nth-child(even) { background-color: #f9fafb; }
          .bonificado { background-color: #fef3c7 !important; }
          .bonificado-tag { color: #b45309; font-weight: bold; font-size: 9pt; }
          .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #d1d5db; font-size: 9pt; color: #6b7280; }
          @media print { body { padding: 10px; } .bonificado { background-color: #fef3c7 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>NOTA ADMINISTRATIVA</h1>
          <p style="font-size: 14pt; color: #4b5563;">${nota.nome}</p>
        </div>
        
        <div class="info-grid">
          <div class="info-item"><strong>Criado por:</strong> ${nota.criado_por_nome}</div>
          <div class="info-item"><strong>Data:</strong> ${new Date(nota.data_criacao).toLocaleString('pt-BR')}</div>
          <div class="info-item"><strong>Recebimentos:</strong> ${nota.recebimentos_ids?.length || 0}</div>
          <div class="info-item"><strong>Produtos:</strong> ${dados.length} ${bonificados.length > 0 ? '(' + bonificados.length + ' bonificado' + (bonificados.length > 1 ? 's' : '') + ')' : ''}</div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>PRODUTO</th>
              <th style="text-align: center;">QTD RECEBIDO</th>
              <th style="text-align: center;">QTD PEDIDO</th>
              <th style="text-align: center;">CUSTO CX</th>
              <th style="text-align: center;">PESO CX</th>
              <th style="text-align: center;">CUSTO R$</th>
              <th style="text-align: center; background-color: #fef3c7;">CUSTO REPASSE 5%</th>
              <th style="text-align: center;">PESO RECEBIDO</th>
              <th style="text-align: center; background-color: #dcfce7;">VALOR FINAL</th>
              <th style="text-align: center;">DIVERGÊNCIA</th>
              <th>FORNECEDOR</th>
            </tr>
          </thead>
          <tbody>
            ${dados.map(item => {
              const divergenciaTexto = item.divergencia > 0 ? '+' + item.divergencia.toFixed(2) + ' kg (excesso)' :
                                       item.divergencia < 0 ? item.divergencia.toFixed(2) + ' kg (falta)' :
                                       '0.00 kg (conforme)';
              const divergenciaStyle = item.divergencia > 0 ? 'color: #2563eb; font-weight: 600;' : 
                                       item.divergencia < 0 ? 'color: #dc2626; font-weight: 600;' : 
                                       'color: #059669; font-weight: 600;';
              
              // Calcular CUSTO REPASSE 5% e VALOR FINAL
              const custoRepasse5 = item.custo_rs * 1.05;
              const valorFinal = custoRepasse5 * item.peso_recebido;
              
              // Verificar se é bonificado
              const isBonificado = bonificados.includes(item.produto_id);
              const rowClass = isBonificado ? 'bonificado' : '';
              const nomeComTag = isBonificado ? '<span class="bonificado-tag">[BONIF.]</span> ' + item.nome : item.nome;
              const valorFinalDisplay = isBonificado 
                ? '<s style="color: #9ca3af;">R$ ' + valorFinal.toFixed(2) + '</s> <span class="bonificado-tag">(BONIFICADO)</span>'
                : 'R$ ' + valorFinal.toFixed(2);
              
              return '<tr class="' + rowClass + '">' +
                '<td><strong>' + nomeComTag + '</strong></td>' +
                '<td style="text-align: center;">' + item.quantidade_recebido + '</td>' +
                '<td style="text-align: center;">' + item.quantidade_pedido + '</td>' +
                '<td style="text-align: center;">R$ ' + item.custo_cx.toFixed(2) + '</td>' +
                '<td style="text-align: center;">' + item.peso_cx.toFixed(2) + ' kg</td>' +
                '<td style="text-align: center;">R$ ' + item.custo_rs.toFixed(2) + '/kg</td>' +
                '<td style="text-align: center; background-color: #fef9e7; font-weight: 600; color: #b45309;">R$ ' + custoRepasse5.toFixed(2) + '/kg</td>' +
                '<td style="text-align: center;">' + item.peso_recebido.toFixed(2) + ' kg</td>' +
                '<td style="text-align: center; background-color: ' + (isBonificado ? '#fef3c7' : '#ecfdf5') + '; font-weight: 700; color: ' + (isBonificado ? '#b45309' : '#047857') + ';">' + valorFinalDisplay + '</td>' +
                '<td style="text-align: center; ' + divergenciaStyle + '">' + divergenciaTexto + '</td>' +
                '<td>' + item.fornecedor + '</td>' +
                '</tr>';
            }).join('')}
          </tbody>
          <tfoot>
            ${bonificados.length > 0 ? '<tr style="background-color: #fef3c7;"><td colspan="8" style="text-align: right; padding: 10px; font-weight: 600; color: #b45309;">VALOR BONIFICAÇÕES (' + bonificados.length + ' produto' + (bonificados.length > 1 ? 's' : '') + '):</td><td style="text-align: center; padding: 10px; font-weight: 700; color: #b45309;">- R$ ' + valorBonificacoes.toFixed(2) + '</td><td colspan="2"></td></tr>' : ''}
            <tr style="background-color: #f3f4f6; border-top: 2px solid #374151;">
              <td colspan="8" style="text-align: right; padding: 12px; font-weight: 700; font-size: 14px;">${bonificados.length > 0 ? 'VALOR TOTAL (SEM BONIFICAÇÕES):' : 'VALOR TOTAL DA NOTA:'}</td>
              <td style="text-align: center; padding: 12px; font-weight: 700; font-size: 16px; color: #047857; background-color: #dcfce7;">
                R$ ${valorTotalLiquido.toFixed(2)}
              </td>
              <td colspan="2"></td>
            </tr>
          </tfoot>
        </table>
        
        <div class="footer">
          <p><strong>Legenda:</strong></p>
          <p>• CUSTO R$ = CUSTO CX ÷ PESO CX</p>
          <p>• CUSTO REPASSE 5% = CUSTO R$ + 5%</p>
          <p>• VALOR FINAL = CUSTO REPASSE 5% × PESO RECEBIDO</p>
          <p>• DIVERGÊNCIA = PESO RECEBIDO - (QTD PEDIDO × PESO CX)</p>
          <p>• Valores positivos indicam excesso, negativos indicam falta</p>
          ${bonificados.length > 0 ? '<p>• <span style="background-color: #fef3c7; padding: 2px 6px;">🎁 BONIFICADO</span> = Produto não contabilizado no valor total</p>' : ''}
          <p>• Dados consolidados de ${nota.recebimentos_ids?.length || 0} recebimento(s)</p>
          <p style="margin-top: 10px; font-style: italic;">Documento gerado em ${new Date().toLocaleString('pt-BR')}</p>
        </div>
        
        <script>window.onload = function() { window.print(); };</script>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  // Carregar dados consolidados ao abrir modal
  React.useEffect(() => {
    if ((showViewModal || showEditModal) && notaSelecionada) {
      const dados = consolidarDadosRecebimentos(notaSelecionada);
      setDadosConsolidados(dados);
      
      // Carregar produtos bonificados salvos na nota
      setProdutosBonificados(notaSelecionada.produtos_bonificados || []);
      setBonificacoesAlteradas(false);
      
      // Inicializar dados editados com TODOS os campos
      if (showEditModal) {
        const editados = {};
        dados.forEach(item => {
          editados[item.produto_id] = {
            custo_cx: item.custo_cx,
            peso_cx: item.peso_cx,
            fornecedor: item.fornecedor,
            peso_recebido: item.peso_recebido,
            quantidade_recebido: item.quantidade_recebido,
            quantidade_pedido: item.quantidade_pedido
          };
        });
        setDadosEditados(editados);
      }
    }
  }, [showViewModal, showEditModal, notaSelecionada]);

  // Função para marcar/desmarcar produto como bonificação
  const toggleBonificacao = (produtoId) => {
    setProdutosBonificados(prev => {
      const novosIds = prev.includes(produtoId)
        ? prev.filter(id => id !== produtoId)
        : [...prev, produtoId];
      return novosIds;
    });
    setBonificacoesAlteradas(true);
  };

  // Função para salvar bonificações
  const handleSalvarBonificacoes = async () => {
    if (!notaSelecionada) return;
    
    try {
      setLoading(true);
      await axios.put(`/notas-administrativas/${notaSelecionada.id}`, {
        produtos_bonificados: produtosBonificados
      });
      
      toast({
        title: "Bonificações salvas",
        description: `${produtosBonificados.length} produto(s) marcado(s) como bonificação`,
      });
      
      setBonificacoesAlteradas(false);
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao salvar",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para salvar edições
  const handleSalvarEdicoes = async () => {
    try {
      const produtos_editados = {};
      const pesos_editados = {};
      
      Object.keys(dadosEditados).forEach(produtoId => {
        const produto = dadosConsolidados.find(p => p.produto_id === produtoId);
        if (produto) {
          const edicoes = {};
          
          // Apenas adicionar campos que foram realmente editados (não undefined e não null)
          if (dadosEditados[produtoId].custo_cx !== undefined && dadosEditados[produtoId].custo_cx !== null) {
            edicoes.custo_cx = dadosEditados[produtoId].custo_cx;
          }
          if (dadosEditados[produtoId].peso_cx !== undefined && dadosEditados[produtoId].peso_cx !== null) {
            edicoes.peso_cx = dadosEditados[produtoId].peso_cx;
          }
          if (dadosEditados[produtoId].fornecedor !== undefined && dadosEditados[produtoId].fornecedor !== null) {
            edicoes.fornecedor = dadosEditados[produtoId].fornecedor;
          }
          if (dadosEditados[produtoId].quantidade_recebido !== undefined && dadosEditados[produtoId].quantidade_recebido !== null) {
            edicoes.quantidade_recebido = dadosEditados[produtoId].quantidade_recebido;
          }
          if (dadosEditados[produtoId].quantidade_pedido !== undefined && dadosEditados[produtoId].quantidade_pedido !== null) {
            edicoes.quantidade_pedido = dadosEditados[produtoId].quantidade_pedido;
          }
          
          produtos_editados[produtoId] = edicoes;
          
          // Se pesoTotal foi editado e há múltiplos recebimentos, salvar pesos individuais
          if (produto.pesosRecebimentos.length > 1 && notaSelecionada.pesos_editados?.[produtoId]) {
            pesos_editados[produtoId] = notaSelecionada.pesos_editados[produtoId];
          }
        }
      });
      
      await axios.put(`/notas-administrativas/${notaSelecionada.id}`, {
        produtos_editados,
        pesos_editados
      });
      
      toast({
        title: "Edições salvas!",
        description: "As alterações foram salvas com sucesso"
      });
      
      setShowEditModal(false);
      setNotaSelecionada(null);
      setDadosEditados({});
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao salvar",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  // Função para editar peso total (abrir sub-modal)
  const handleEditarPesoTotal = (produto) => {
    setProdutoEditandoPesos(produto);
    setPesosEditados(produto.pesosRecebimentos.map(pr => ({ ...pr })));
    setShowEditPesosModal(true);
  };

  // Função para salvar pesos individuais
  const handleSalvarPesos = async () => {
    const pesoTotal = pesosEditados.reduce((sum, pr) => sum + parseFloat(pr.peso || 0), 0);
    
    // Atualizar dadosEditados com novo peso total
    setDadosEditados({
      ...dadosEditados,
      [produtoEditandoPesos.produto_id]: {
        ...dadosEditados[produtoEditandoPesos.produto_id],
        pesoTotal: pesoTotal
      }
    });
    
    // Salvar pesos editados na nota
    try {
      const pesos_editados = {
        ...(notaSelecionada.pesos_editados || {}),
        [produtoEditandoPesos.produto_id]: pesosEditados.map(pe => ({
          recebimento_id: pe.recebimento_id,
          peso: parseFloat(pe.peso || 0)
        }))
      };
      
      await axios.put(`/notas-administrativas/${notaSelecionada.id}`, {
        pesos_editados
      });
      
      toast({
        title: "Pesos atualizados!",
        description: "Os pesos foram atualizados com sucesso"
      });
      
      setShowEditPesosModal(false);
      setProdutoEditandoPesos(null);
      setPesosEditados([]);
      
      // Recarregar dados
      const notaAtualizada = await axios.get(`/notas-administrativas/${notaSelecionada.id}`);
      setNotaSelecionada(notaAtualizada.data);
      
      const dados = consolidarDadosRecebimentos(notaAtualizada.data);
      setDadosConsolidados(dados);
    } catch (error) {
      toast({
        title: "Erro ao salvar pesos",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Notas Administrativas</h3>
        <p className="text-sm text-gray-600 mb-4">
          Gerencie notas administrativas consolidando dados de múltiplos recebimentos
        </p>
      </div>

      {!showAddForm ? (
        <div>
          <button
            onClick={() => setShowAddForm(true)}
            className="mb-6 flex items-center space-x-2 px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors"
          >
            <span>➕</span>
            <span>Nova Nota</span>
          </button>

          {notasAdministrativas.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-4xl mb-4">📊</div>
              <p>Nenhuma nota administrativa criada ainda.</p>
              <p className="text-sm mt-2">Clique em "Nova Nota" para começar</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {notasAdministrativas.map((nota) => (
                <div key={nota.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{nota.nome}</h4>
                  <div className="text-sm text-gray-600 mb-4">
                    <p><span className="font-medium">Criado por:</span> {nota.criado_por_nome}</p>
                    <p><span className="font-medium">Data:</span> {new Date(nota.data_criacao).toLocaleDateString('pt-BR')}</p>
                    <p><span className="font-medium">Recebimentos:</span> {nota.recebimentos_ids?.length || 0}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setNotaSelecionada(nota);
                        setShowViewModal(true);
                      }}
                      className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm font-medium"
                    >
                      Visualizar
                    </button>
                    <button
                      onClick={() => handleExcluirNota(nota)}
                      className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm font-medium"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          <button
            onClick={() => {
              setShowAddForm(false);
              setNomeNota('');
              setRecebimentosSelecionados([]);
              setCotacaoSelecionada('');
            }}
            className="flex items-center space-x-2 text-green-700 hover:text-green-800 mb-4"
          >
            <span>←</span>
            <span>Voltar</span>
          </button>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Nova Nota Administrativa</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome da Nota *
              </label>
              <input
                type="text"
                value={nomeNota}
                onChange={(e) => setNomeNota(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                placeholder="Ex: Nota Administrativa Janeiro 2025"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selecione uma Cotação (Opcional)
              </label>
              <select
                value={cotacaoSelecionada}
                onChange={(e) => setCotacaoSelecionada(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
              >
                <option value="">Nenhuma cotação vinculada</option>
                {cotacoes
                  .filter(c => c.status_cotacao === 'Ativa' || c.status_cotacao === 'Encerrada')
                  .map(cotacao => {
                    return (
                      <option key={cotacao.id} value={cotacao.id}>
                        {cotacao.nome || cotacao.numero || `Cotação ${cotacao.id.substring(0, 8)}`}
                      </option>
                    );
                  })}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Vincule uma cotação para facilitar o rastreamento e controle dos dados da nota.
              </p>
            </div>

            <div className="mb-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Selecionar Recebimentos Finalizados ({recebimentosFinalizados.length} disponíveis)
              </h4>

              {recebimentosFinalizados.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">📋</div>
                  <p>Nenhum recebimento finalizado disponível</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                  {recebimentosFinalizados.map((recebimento) => (
                    <div
                      key={recebimento.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        recebimentosSelecionados.includes(recebimento.id)
                          ? 'border-green-600 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        if (recebimentosSelecionados.includes(recebimento.id)) {
                          setRecebimentosSelecionados(recebimentosSelecionados.filter(id => id !== recebimento.id));
                        } else {
                          setRecebimentosSelecionados([...recebimentosSelecionados, recebimento.id]);
                        }
                      }}
                    >
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          checked={recebimentosSelecionados.includes(recebimento.id)}
                          onChange={() => {}}
                          className="mt-1 mr-3"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">Pedido #{recebimento.numero_pedido}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            <p>Filial: {recebimento.filial}</p>
                            <p>Produtos: {recebimento.itens?.length || 0}</p>
                            <p>Conferido em: {new Date(recebimento.conferido_em).toLocaleDateString('pt-BR')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNomeNota('');
                  setRecebimentosSelecionados([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCriarNota}
                disabled={!nomeNota.trim() || recebimentosSelecionados.length === 0}
                className={`px-4 py-2 rounded-lg font-medium ${
                  nomeNota.trim() && recebimentosSelecionados.length > 0
                    ? 'bg-green-700 text-white hover:bg-green-800'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Criar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização */}
      {showViewModal && notaSelecionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => { setShowViewModal(false); setSearchText(''); }}>
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">{notaSelecionada.nome}</h3>
                <button onClick={() => { setShowViewModal(false); setSearchText(''); }} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-gray-900">Produtos Consolidados</h4>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setOrdenacaoFornecedor(!ordenacaoFornecedor)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                      ordenacaoFornecedor
                        ? 'bg-green-100 border-green-500 text-green-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>{ordenacaoFornecedor ? '✓' : '↕️'}</span>
                    <span>Ordenar por Fornecedor</span>
                  </button>
                  <button
                    onClick={() => exportarNotaParaImpressao(notaSelecionada, getDadosOrdenados())}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800"
                  >
                    <span>🖨️</span>
                    <span>Exportar para Impressão</span>
                  </button>
                </div>
              </div>

              {/* Barra de pesquisa com sticky */}
              <div className="sticky top-0 z-10 bg-white pb-4 mb-4 border-b border-gray-200">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Pesquisar produto por nome..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                  />
                  <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
                  {searchText && (
                    <button
                      onClick={() => setSearchText('')}
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              
              {/* Filtrar dados baseado na pesquisa */}
              {(() => {
                const dadosFiltrados = getDadosOrdenados().filter(item => 
                  item.nome.toLowerCase().includes(searchText.toLowerCase())
                );

                if (dadosFiltrados.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-lg">Nenhum produto encontrado.</p>
                    </div>
                  );
                }

                return (
                  <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">BONIF.</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PRODUTO</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">QTD RECEBIDO</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">QTD PEDIDO</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">CUSTO CX</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">PESO CX</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">CUSTO R$</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase bg-amber-100">CUSTO REPASSE 5%</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">PESO RECEBIDO</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase bg-green-100">VALOR FINAL</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">DIVERGÊNCIA</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">FORNECEDOR</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dadosFiltrados.map((item, index) => {
                      const divergenciaClass = item.divergencia > 0 ? 'text-blue-600 font-semibold' : 
                                              item.divergencia < 0 ? 'text-red-600 font-semibold' : 
                                              'text-green-600 font-semibold';
                      const divergenciaTexto = item.divergencia > 0 ? `+${item.divergencia.toFixed(2)} kg (excesso)` :
                                               item.divergencia < 0 ? `${item.divergencia.toFixed(2)} kg (falta)` :
                                               '0.00 kg (conforme)';
                      
                      const filiais = agruparPorFilial(item);
                      const isExpanded = expandedProducts.has(item.produto_id);
                      const temMultiplasFiliais = filiais.length > 1;
                      
                      // Calcular CUSTO REPASSE 5% e VALOR FINAL
                      const custoRepasse5 = item.custo_rs * 1.05; // +5%
                      const valorFinal = custoRepasse5 * item.peso_recebido;
                      
                      // Verificar se produto está marcado como bonificação
                      const isBonificado = produtosBonificados.includes(item.produto_id);
                      
                      return (
                        <React.Fragment key={item.produto_id}>
                          <tr className={isBonificado ? 'bg-yellow-100 hover:bg-yellow-200' : (index % 2 === 0 ? 'bg-white hover:bg-gray-100' : 'bg-gray-50 hover:bg-gray-100')}>
                            <td className="px-3 py-4 text-center">
                              <button
                                onClick={() => toggleBonificacao(item.produto_id)}
                                className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
                                  isBonificado 
                                    ? 'bg-yellow-400 border-yellow-600 text-yellow-800' 
                                    : 'bg-white border-gray-300 text-gray-400 hover:border-yellow-400 hover:text-yellow-600'
                                }`}
                                title={isBonificado ? 'Remover bonificação' : 'Marcar como bonificação'}
                              >
                                {isBonificado ? '🎁' : '○'}
                              </button>
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              <div className="flex items-center gap-2">
                                {isBonificado && <span className="text-yellow-600 font-bold">[BONIF.]</span>}
                                {item.nome}
                              </div>
                              {/* Mostrar filial de forma minimalista se há apenas 1 */}
                              {!temMultiplasFiliais && filiais.length === 1 && (
                                <div className="text-xs text-gray-500 mt-1">
                                  📍 {filiais[0].filial}
                                </div>
                              )}
                              {/* Botão "ver filiais" se há múltiplas filiais */}
                              {temMultiplasFiliais && (
                                <button
                                  onClick={() => {
                                    const newExpanded = new Set(expandedProducts);
                                    if (isExpanded) {
                                      newExpanded.delete(item.produto_id);
                                    } else {
                                      newExpanded.add(item.produto_id);
                                    }
                                    setExpandedProducts(newExpanded);
                                  }}
                                  className="mt-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  {isExpanded ? '▼ esconder filiais' : '▶ ver filiais'} ({filiais.length})
                                </button>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 text-center">{item.quantidade_recebido}</td>
                            <td className="px-6 py-4 text-sm text-gray-900 text-center">{item.quantidade_pedido}</td>
                            <td className="px-6 py-4 text-sm text-gray-900 text-center">R$ {item.custo_cx.toFixed(2)}</td>
                            <td className="px-6 py-4 text-sm text-gray-900 text-center">{item.peso_cx.toFixed(2)} kg</td>
                            <td className="px-6 py-4 text-sm font-semibold text-green-700 text-center">R$ {item.custo_rs.toFixed(2)}/kg</td>
                            <td className="px-6 py-4 text-sm font-semibold text-amber-700 text-center bg-amber-50">R$ {custoRepasse5.toFixed(2)}/kg</td>
                            <td className="px-6 py-4 text-sm text-gray-900 text-center">{item.peso_recebido.toFixed(2)} kg</td>
                            <td className="px-6 py-4 text-sm font-bold text-green-700 text-center bg-green-50">R$ {valorFinal.toFixed(2)}</td>
                            <td className="px-6 py-4 text-sm text-center">
                              <span className={divergenciaClass}>{divergenciaTexto}</span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">{item.fornecedor}</td>
                          </tr>
                          {/* Linha expandida com detalhes por filial */}
                          {isExpanded && temMultiplasFiliais && (
                            <tr className="bg-blue-50">
                              <td colSpan="12" className="px-6 py-4">
                                <div className="text-sm font-semibold text-gray-700 mb-2">Detalhes por Filial:</div>
                                <table className="min-w-full border border-gray-300">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">FILIAL</th>
                                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-600">QTD RECEBIDO</th>
                                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-600">QTD PEDIDO</th>
                                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-600">PESO RECEBIDO</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filiais.map((filial, idx) => (
                                      <tr key={idx} className="border-t border-gray-200">
                                        <td className="px-4 py-2 text-sm text-gray-900">📍 {filial.filial}</td>
                                        <td className="px-4 py-2 text-sm text-gray-900 text-center">{filial.quantidade_recebido}</td>
                                        <td className="px-4 py-2 text-sm text-gray-900 text-center">{filial.quantidade_pedido}</td>
                                        <td className="px-4 py-2 text-sm text-gray-900 text-center">{filial.peso_recebido.toFixed(2)} kg</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                    {/* Linha de bonificações se houver */}
                    {produtosBonificados.length > 0 && (
                      <tr className="bg-yellow-50">
                        <td colSpan="9" className="px-6 py-3 text-right text-sm font-semibold text-yellow-700">
                          VALOR BONIFICAÇÕES ({produtosBonificados.length} produto{produtosBonificados.length > 1 ? 's' : ''}):
                        </td>
                        <td className="px-6 py-3 text-center text-sm font-bold text-yellow-700 bg-yellow-100">
                          - R$ {dadosFiltrados.filter(item => produtosBonificados.includes(item.produto_id)).reduce((total, item) => {
                            const custoRepasse5 = item.custo_rs * 1.05;
                            const valorFinal = custoRepasse5 * item.peso_recebido;
                            return total + valorFinal;
                          }, 0).toFixed(2)}
                        </td>
                        <td colSpan="2" className="px-6 py-3"></td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan="9" className="px-6 py-4 text-right text-sm font-bold text-gray-900 uppercase">
                        {produtosBonificados.length > 0 ? 'VALOR TOTAL (SEM BONIFICAÇÕES):' : 'VALOR TOTAL DA NOTA:'}
                      </td>
                      <td className="px-6 py-4 text-center text-lg font-bold text-green-700 bg-green-100">
                        R$ {dadosFiltrados.filter(item => !produtosBonificados.includes(item.produto_id)).reduce((total, item) => {
                          const custoRepasse5 = item.custo_rs * 1.05;
                          const valorFinal = custoRepasse5 * item.peso_recebido;
                          return total + valorFinal;
                        }, 0).toFixed(2)}
                      </td>
                      <td colSpan="2" className="px-6 py-4"></td>
                    </tr>
                  </tfoot>
                </table>
                
                {/* Botão de Salvar Bonificações */}
                {bonificacoesAlteradas && (
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2 text-yellow-800">
                      <span className="text-xl">🎁</span>
                      <span className="font-medium">
                        {produtosBonificados.length} produto(s) marcado(s) como bonificação
                      </span>
                    </div>
                    <button
                      onClick={handleSalvarBonificacoes}
                      disabled={loading}
                      className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg shadow transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Salvando...' : '💾 Salvar Bonificações'}
                    </button>
                  </div>
                )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição */}
      {showEditModal && notaSelecionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => { setShowEditModal(false); setSearchText(''); }}>
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Editar: {notaSelecionada.nome}</h3>
                <button onClick={() => { setShowEditModal(false); setSearchText(''); }} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Colunas editáveis:</strong> QTD RECEBIDO, QTD PEDIDO, CUSTO CX, PESO CX, FORNECEDOR, PESO RECEBIDO
                  <br/>
                  <strong>Colunas automáticas:</strong> PRODUTO, CUSTO R$ (calculado), DIVERGÊNCIA (calculada automaticamente)
                  <br/>
                  <strong>💡 Dica:</strong> Ao editar QTD PEDIDO, a DIVERGÊNCIA será recalculada automaticamente usando a fórmula: PESO RECEBIDO - (QTD PEDIDO × PESO CX)
                </p>
              </div>
              
              <div className="mb-4">
                <button
                  onClick={() => setOrdenacaoFornecedor(!ordenacaoFornecedor)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                    ordenacaoFornecedor
                      ? 'bg-green-100 border-green-500 text-green-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>{ordenacaoFornecedor ? '✓' : '↕️'}</span>
                  <span>Ordenar por Fornecedor</span>
                </button>
              </div>

              {/* Barra de pesquisa com sticky */}
              <div className="sticky top-0 z-10 bg-white pb-4 mb-4 border-b border-gray-200">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Pesquisar produto por nome..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                  />
                  <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
                  {searchText && (
                    <button
                      onClick={() => setSearchText('')}
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              
              {/* Filtrar dados baseado na pesquisa */}
              {(() => {
                const dadosFiltrados = getDadosOrdenados().filter(item => 
                  item.nome.toLowerCase().includes(searchText.toLowerCase())
                );

                if (dadosFiltrados.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-lg">Nenhum produto encontrado.</p>
                    </div>
                  );
                }

                return (
                  <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">PRODUTO</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">QTD RECEBIDO</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">QTD PEDIDO</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">CUSTO CX</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">PESO CX</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">CUSTO R$</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">PESO RECEBIDO</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">DIVERGÊNCIA</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">FORNECEDOR</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dadosFiltrados.map((item, index) => {
                      // Inicializar dados com todos os valores do item se ainda não existir
                      const dados = dadosEditados[item.produto_id] || {
                        custo_cx: item.custo_cx,
                        peso_cx: item.peso_cx,
                        fornecedor: item.fornecedor,
                        peso_recebido: item.peso_recebido,
                        quantidade_recebido: item.quantidade_recebido,
                        quantidade_pedido: item.quantidade_pedido
                      };
                      const custoRsCalculado = dados.peso_cx > 0 ? dados.custo_cx / dados.peso_cx : 0;
                      
                      // Calcular divergência em tempo real: PESO RECEBIDO - (QTD PEDIDO × PESO CX)
                      const pesoRecebidoAtual = dados.peso_recebido !== undefined ? dados.peso_recebido : item.peso_recebido;
                      const quantidadePedidoAtual = dados.quantidade_pedido !== undefined ? dados.quantidade_pedido : item.quantidade_pedido;
                      const pesoCxAtual = dados.peso_cx !== undefined ? dados.peso_cx : item.peso_cx;
                      const divergenciaCalculada = pesoRecebidoAtual - (quantidadePedidoAtual * pesoCxAtual);
                      const divergenciaClass = divergenciaCalculada > 0 ? 'text-blue-600 font-semibold' : 
                                              divergenciaCalculada < 0 ? 'text-red-600 font-semibold' : 
                                              'text-green-600 font-semibold';
                      const divergenciaTexto = divergenciaCalculada > 0 ? `+${divergenciaCalculada.toFixed(2)} kg (excesso)` :
                                               divergenciaCalculada < 0 ? `${divergenciaCalculada.toFixed(2)} kg (falta)` :
                                               '0.00 kg (conforme)';
                      
                      return (
                        <tr key={item.produto_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-3 py-3 text-sm font-medium text-gray-900">{item.nome}</td>
                          <td className="px-3 py-3 text-center">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={dados.quantidade_recebido !== undefined && dados.quantidade_recebido !== null ? dados.quantidade_recebido : (item.quantidade_recebido !== undefined && item.quantidade_recebido !== null ? item.quantidade_recebido : 0)}
                              onChange={(e) => setDadosEditados({
                                ...dadosEditados,
                                [item.produto_id]: {
                                  custo_cx: dados.custo_cx !== undefined ? dados.custo_cx : item.custo_cx,
                                  peso_cx: dados.peso_cx !== undefined ? dados.peso_cx : item.peso_cx,
                                  fornecedor: dados.fornecedor || item.fornecedor,
                                  peso_recebido: dados.peso_recebido !== undefined ? dados.peso_recebido : item.peso_recebido,
                                  quantidade_pedido: dados.quantidade_pedido !== undefined ? dados.quantidade_pedido : item.quantidade_pedido,
                                  quantidade_recebido: parseInt(e.target.value) || 0
                                }
                              })}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                            />
                          </td>
                          <td className="px-3 py-3 text-center">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={dados.quantidade_pedido !== undefined && dados.quantidade_pedido !== null ? dados.quantidade_pedido : (item.quantidade_pedido !== undefined && item.quantidade_pedido !== null ? item.quantidade_pedido : 0)}
                              onChange={(e) => {
                                const novoPedido = parseInt(e.target.value) || 0;
                                setDadosEditados({
                                  ...dadosEditados,
                                  [item.produto_id]: {
                                    custo_cx: dados.custo_cx !== undefined ? dados.custo_cx : item.custo_cx,
                                    peso_cx: dados.peso_cx !== undefined ? dados.peso_cx : item.peso_cx,
                                    fornecedor: dados.fornecedor || item.fornecedor,
                                    peso_recebido: dados.peso_recebido !== undefined ? dados.peso_recebido : item.peso_recebido,
                                    quantidade_recebido: dados.quantidade_recebido !== undefined ? dados.quantidade_recebido : item.quantidade_recebido,
                                    quantidade_pedido: novoPedido
                                  }
                                });
                              }}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                            />
                          </td>
                          <td className="px-3 py-3 text-center">
                            <input
                              type="number"
                              step="0.01"
                              value={dados.custo_cx !== undefined ? dados.custo_cx : item.custo_cx}
                              onChange={(e) => setDadosEditados({
                                ...dadosEditados,
                                [item.produto_id]: {
                                  custo_cx: parseFloat(e.target.value) || 0,
                                  peso_cx: dados.peso_cx !== undefined ? dados.peso_cx : item.peso_cx,
                                  fornecedor: dados.fornecedor || item.fornecedor,
                                  peso_recebido: dados.peso_recebido !== undefined ? dados.peso_recebido : item.peso_recebido,
                                  quantidade_recebido: dados.quantidade_recebido !== undefined ? dados.quantidade_recebido : item.quantidade_recebido,
                                  quantidade_pedido: dados.quantidade_pedido !== undefined ? dados.quantidade_pedido : item.quantidade_pedido
                                }
                              })}
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                            />
                          </td>
                          <td className="px-3 py-3 text-center">
                            <input
                              type="number"
                              step="0.01"
                              value={dados.peso_cx !== undefined ? dados.peso_cx : item.peso_cx}
                              onChange={(e) => setDadosEditados({
                                ...dadosEditados,
                                [item.produto_id]: {
                                  custo_cx: dados.custo_cx !== undefined ? dados.custo_cx : item.custo_cx,
                                  peso_cx: parseFloat(e.target.value) || 0,
                                  fornecedor: dados.fornecedor || item.fornecedor,
                                  peso_recebido: dados.peso_recebido !== undefined ? dados.peso_recebido : item.peso_recebido,
                                  quantidade_recebido: dados.quantidade_recebido !== undefined ? dados.quantidade_recebido : item.quantidade_recebido,
                                  quantidade_pedido: dados.quantidade_pedido !== undefined ? dados.quantidade_pedido : item.quantidade_pedido
                                }
                              })}
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                            />
                          </td>
                          <td className="px-3 py-3 text-sm font-semibold text-green-700 text-center">
                            R$ {custoRsCalculado.toFixed(2)}/kg
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <span className="text-sm">{dados.peso_recebido?.toFixed(2) || item.peso_recebido.toFixed(2)} kg</span>
                              {item.pesosRecebimentos.length > 1 && (
                                <button
                                  onClick={() => handleEditarPesoTotal(item)}
                                  className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                >
                                  ✏️
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-sm text-center">
                            <span className={divergenciaClass}>{divergenciaTexto}</span>
                          </td>
                          <td className="px-3 py-3">
                            <input
                              type="text"
                              value={dados.fornecedor || item.fornecedor || ''}
                              onChange={(e) => setDadosEditados({
                                ...dadosEditados,
                                [item.produto_id]: {
                                  custo_cx: dados.custo_cx !== undefined ? dados.custo_cx : item.custo_cx,
                                  peso_cx: dados.peso_cx !== undefined ? dados.peso_cx : item.peso_cx,
                                  fornecedor: e.target.value,
                                  peso_recebido: dados.peso_recebido !== undefined ? dados.peso_recebido : item.peso_recebido,
                                  quantidade_recebido: dados.quantidade_recebido !== undefined ? dados.quantidade_recebido : item.quantidade_recebido,
                                  quantidade_pedido: dados.quantidade_pedido !== undefined ? dados.quantidade_pedido : item.quantidade_pedido
                                }
                              })}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                  </div>
                );
              })()}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => { setShowEditModal(false); setSearchText(''); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSalvarEdicoes}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sub-modal de Edição de Pesos */}
      {showEditPesosModal && produtoEditandoPesos && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]" onClick={() => setShowEditPesosModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Editar Peso Total - {produtoEditandoPesos.nome}</h3>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Edite o peso de cada recebimento individualmente. A soma será calculada automaticamente.
              </p>
              
              <div className="space-y-3">
                {pesosEditados.map((pesoReceb, index) => (
                  <div key={pesoReceb.recebimento_id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{pesoReceb.filial || 'Sem filial'}</p>
                      <p className="text-sm text-gray-500">Pedido #{pesoReceb.numero_pedido}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        step="0.01"
                        value={pesoReceb.peso}
                        onChange={(e) => {
                          const newPesos = [...pesosEditados];
                          newPesos[index].peso = parseFloat(e.target.value) || 0;
                          setPesosEditados(newPesos);
                        }}
                        className="w-32 px-3 py-2 border border-gray-300 rounded text-right"
                      />
                      <span className="text-gray-600">kg</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">Peso Total:</span>
                  <span className="text-2xl font-bold text-green-700">
                    {pesosEditados.reduce((sum, pr) => sum + (parseFloat(pr.peso) || 0), 0).toFixed(2)} kg
                  </span>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowEditPesosModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSalvarPesos}
                  className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 font-medium"
                >
                  Salvar Pesos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============= GESTÃO PEDIDOS TAB =============

export default AdministrativoTab;
